import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Button, Callout, Field, inputClass } from '@/components/ui';

/**
 * Ask for a reset link. FR-01.
 *
 * The success state says the same thing whatever happened, because the server
 * answers the same thing whatever happened. A page that says "no account with
 * that email" is a tool for finding out who has an account.
 */
export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get('email') ?? '').trim();

    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Use an email address we can reply to.');
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null;
        setError(body?.error?.message ?? 'That did not go through. Try again.');
        return;
      }
      setSent(true);
    } catch {
      setError('That did not go through. Check your connection.');
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="mx-auto max-w-[440px] px-5 py-16">
        <h1 className="text-[32px] font-semibold tracking-[-0.02em]">Check your email</h1>
        <p className="mt-2 text-[15px] text-muted">
          If that address has an account, a reset link is on its way. It works once and
          expires in an hour.
        </p>
        <p className="mt-8 text-[15px] text-muted">
          <Link to="/auth/sign-in" className="font-semibold underline">
            Back to sign in
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[440px] px-5 py-16">
      <h1 className="text-[32px] font-semibold tracking-[-0.02em]">Forgotten your password?</h1>
      <p className="mt-2 text-[15px] text-muted">
        Give us the address you sign in with and we will send you a link to choose a new
        password.
      </p>

      <form onSubmit={submit} noValidate className="mt-8 space-y-6">
        {error && <Callout tone="danger" title={error} />}

        <Field label="Email" htmlFor="email">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            className={inputClass}
            aria-invalid={Boolean(error)}
          />
        </Field>

        <Button type="submit" size="lg" disabled={submitting}>
          {submitting ? 'Sending' : 'Send the link'}
        </Button>

        <p className="text-[15px] text-muted">
          <Link to="/auth/sign-in" className="font-semibold underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
